import React from 'react';
import { graphql } from '../../generated';
import { t } from '../../locales/translate';
import { useModal } from '../Modal/PresentedModal';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { Automation } from '../../generated/graphql';
import { FormDatepicker } from '../Form/Datepicker/FormDatepicker';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError } from '../../model/notification';
import { FormForm } from '../Form/FormForm';
import { AutomatableType } from './automations';
import { QueryNames } from '../../model/graphql';
import { parseDate } from '../../util/datetime';

const AUTOMATIONS_FOR_CHANGE = graphql(`
  query AutomationsForChange($id: ID!, $type: AutomationAutomatableType!) {
    automations(id: $id, type: $type) {
      nodes {
        id
        executeAt
        operation
        permissions {
          id
          update
          destroy
        }
      }
    }
  }
`);

const CREATE_AUTOMATION = graphql(`
  mutation CreateAutomation(
    $automatableId: ID!
    $automatableType: AutomationAutomatableType!
    $executeAt: ISO8601DateTime!
    $operation: AutomationOperation!
  ) {
    createAutomation(
      input: {
        automatableId: $automatableId
        automatableType: $automatableType
        executeAt: $executeAt
        operation: $operation
      }
    ) {
      automation {
        id
      }
    }
  }
`);

const UPDATE_AUTOMATION = graphql(`
  mutation UpdateAutomation($id: ID!, $executeAt: ISO8601DateTime!) {
    updateAutomation(input: { id: $id, executeAt: $executeAt }) {
      automation {
        id
      }
    }
  }
`);

const DESTROY_AUTOMATION = graphql(`
  mutation DestroyAutomation($id: ID!) {
    destroyAutomation(input: { id: $id }) {
      clientMutationId
    }
  }
`);

interface Props {
  automatableId: string;
  automatableType: AutomatableType;
}

export function AutomationForm({ automatableId, automatableType }: Props): JSX.Element {
  const { data } = useSuspenseQuery(AUTOMATIONS_FOR_CHANGE, {
    variables: { id: automatableId, type: automatableType },
    fetchPolicy: 'no-cache',
  });
  const { open, Modal, close } = useModal();

  const [create] = useMutation(CREATE_AUTOMATION, {
    refetchQueries: [QueryNames[automatableType]],
  });
  const [update] = useMutation(UPDATE_AUTOMATION, { refetchQueries: [QueryNames[automatableType]] });
  const [destroy] = useMutation(DESTROY_AUTOMATION, { refetchQueries: [QueryNames[automatableType]] });

  const startAutomation = data?.automations.nodes.find((automation) => automation.operation === 'START');
  const endAutomation = data?.automations.nodes.find((automation) => automation.operation === 'STOP');

  const { fields, dirty, valid, onSubmit } = useForm<{ startDate?: Date; endDate?: Date }>({
    model: {
      startDate: parseDate(startAutomation?.executeAt),
      endDate: parseDate(endAutomation?.executeAt),
    },
    onSubmit: async ({ model }) => {
      const baseVariables = {
        automatableId,
        automatableType,
      };

      if (startAutomation?.id) {
        if (model.startDate) {
          const variables = {
            id: startAutomation?.id,
            executeAt: model.startDate.toISOString(),
          };
          await update({ variables });
        } else {
          await destroy({ variables: { id: startAutomation?.id } });
        }
      } else if (model.startDate) {
        const variables = {
          ...baseVariables,
          executeAt: model.startDate.toISOString(),
          operation: 'START',
        } as const;
        await create({ variables });
      }

      if (endAutomation?.id) {
        if (model.endDate) {
          const variables = {
            id: endAutomation?.id,
            executeAt: model.endDate.toISOString(),
          };

          await update({ variables });
        } else {
          await destroy({ variables: { id: endAutomation?.id } });
        }
      } else if (model.endDate) {
        const variables = {
          ...baseVariables,
          executeAt: model.endDate.toISOString(),
          operation: 'STOP',
        } as const;
        await create({ variables });
      }

      close();
    },
    onSubmitError: handleError,
  });

  return (
    <Modal open={open} title={t.model.automation.editAutomation} size="xs">
      <FormForm onSubmit={onSubmit} onClose={() => close()} canSubmit={dirty && valid}>
        <FormDatepicker
          {...fields.startDate}
          name="startCampaignAt"
          label={t.model.automation.startCampaignAt}
          hourpicker
          disabledDays={[{ before: new Date() }]}
          disabled={disableAutomation(startAutomation)}
        />
        <FormDatepicker
          {...fields.endDate}
          name="stopCampaignAt"
          label={t.model.automation.stopCampaignAt}
          hourpicker
          disabledDays={[{ before: new Date() }]}
          disabled={disableAutomation(endAutomation)}
        />
      </FormForm>
    </Modal>
  );
}

function disableAutomation(automation?: Automation): boolean {
  if (!automation) return false;

  if (!automation.permissions.update) return true;
  if (!automation.permissions.destroy) return true;

  return false;
}
